import React, { useCallback, useEffect, useState } from "react";
import { FileBadge, Printer, Plus, Trash2, UserPlus, BookUser } from "lucide-react";
import { DocumentButton } from "../DocumentButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KINDS = [["societa", "Società con amministratore"], ["affittuario", "Autorizzazione all'affittuario"],
               ["proprietario", "Proprietario"], ["comproprietari", "Più proprietari"]];
const PERSON_FIELDS = [
  ["declarant_name", "Nome e cognome del dichiarante"], ["fiscal_code", "Codice fiscale"],
  ["birth_place", "Nato/a a"], ["birth_date", "Nato/a il"], ["residence", "Residente in"],
];
const COMPANY_FIELDS = [
  ["company_role", "In qualità di"], ["company_name", "Ragione sociale"], ["company_vat", "C.F. / P. IVA società"],
  ["company_seat", "Sede legale società"], ["company_cciaa", "Iscritta alla C.C.I.A.A. di"], ["company_pec", "PEC società"],
];
const ASSET_FIELDS = [
  ["property_type", "Tipo di immobile"], ["property_address", "Indirizzo"], ["property_locality", "Località"],
  ["municipality", "Comune"], ["cadastral_sheet", "Foglio"], ["cadastral_parcel", "Particella"], ["cadastral_sub", "Sub"],
];
const LEASE_FIELDS = [
  ["lease_number", "Contratto di locazione n°"], ["lease_date", "Stipulato il"],
  ["lease_registration", "Registrazione"], ["owner_name", "Proprietario"], ["owner_fiscal_code", "C.F. proprietario"],
];
const TENANT_FIELDS = [
  ["tenant_name", "Affittuario autorizzato (cognome e nome)"], ["tenant_role", "In qualità di"],
  ["tenant_company", "Società dell'affittuario"], ["tenant_vat", "Partita I.V.A. affittuario"],
  ["tenant_seat", "Sede legale affittuario"],
];
const RENT_LEASE_FIELDS = [
  ["lease_number", "Contratto di locazione n° (facoltativo)"], ["lease_date", "Stipulato il"],
  ["lease_registration", "Registrazione"],
];
const CO_OWNER_FIELDS = [["name", "Nome e cognome"], ["fiscal_code", "Codice fiscale"],
                         ["birth_place", "Nato/a a"], ["birth_date", "Nato/a il"], ["residence", "Residente in"]];
const emptyOwner = () => ({ uid: `owner-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, name: "", gender: "M", fiscal_code: "", birth_place: "", birth_date: "", residence: "" });

export const PropertyDeclaration = ({ practice, onSaveCadastral }) => {
  const [form, setForm] = useState({
    kind: "proprietario", gender: "M", property_type: "immobile", company_title: "proprietaria",
    declarant_name: practice.client_name || "", fiscal_code: practice.client_fiscal_code || "",
    birth_place: "", birth_date: "", residence: "",
    company_role: "", company_name: "", company_vat: "", company_seat: "", company_cciaa: "", company_pec: "",
    property_address: practice.site_address || "", property_locality: practice.property_locality || "",
    municipality: practice.municipality || "", cadastral_sheet: practice.cadastral_sheet || "",
    cadastral_parcel: practice.cadastral_parcel || "", cadastral_sub: practice.cadastral_sub || "",
    lease_number: "", lease_date: "", lease_registration: "", owner_name: "", owner_fiscal_code: "",
    authorize_third_party: false, third_party_name: "", third_party_detail: "", extra_statement: "",
    co_owners: [],
  });
  const [directory, setDirectory] = useState([]);
  const [importId, setImportId] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const request = useCallback((path, options = {}) => fetch(`${API}${path}`, {
    credentials: "include", ...options,
    headers: { "Content-Type": "application/json" },
  }), []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const response = await request(`/practices/${practice.id}/declaration-prefill`);
        if (!response.ok || !active) return;
        const data = await response.json();
        if (!active) return;
        setDirectory(data.directory || []);
        const client = data.client || {};
        setForm(f => ({
          ...f,
          declarant_name: f.declarant_name || client.name || "",
          fiscal_code: f.fiscal_code || client.fiscal_code || "",
          birth_place: f.birth_place || client.birth_place || "",
          birth_date: f.birth_date || client.birth_date || "",
          residence: f.residence || client.residence || "",
          company_role: f.company_role || client.company_role || "",
          company_name: f.company_name || client.company_name || "",
          company_vat: f.company_vat || client.company_vat || "",
          company_seat: f.company_seat || client.company_seat || "",
          company_cciaa: f.company_cciaa || client.company_cciaa || "",
          company_pec: f.company_pec || client.company_pec || "",
          ...Object.fromEntries(Object.entries(data.property || {}).filter(([key, value]) => value && !f[key])),
        }));
      } catch { /* prefill facoltativo */ }
    })();
    return () => { active = false; };
  }, [practice.id, request]);

  const company = form.kind === "societa";
  const rentOut = form.kind === "affittuario";
  const ownerCompany = rentOut && !!form.owner_is_company;
  const tenant = company && form.company_title === "affittuaria";
  const multi = form.kind === "comproprietari";
  const allowThirdParty = company;

  const setOwner = (uid, field, value) =>
    setForm(f => ({ ...f, co_owners: f.co_owners.map(o => (o.uid === uid ? { ...o, [field]: value } : o)) }));
  const removeOwner = uid => setForm(f => ({ ...f, co_owners: f.co_owners.filter(o => o.uid !== uid) }));

  function importOwner() {
    const picked = directory.find(item => item.client_id === importId);
    if (!picked) return;
    setForm(f => ({ ...f, co_owners: [...f.co_owners, { ...emptyOwner(), name: picked.name, fiscal_code: picked.fiscal_code || "",
      birth_place: picked.birth_place || "", birth_date: picked.birth_date || "", residence: picked.residence || "" }] }));
    setImportId("");
    setNotice(`${picked.name} importato dall'anagrafica`);
  }

  async function saveOwnerToDirectory(owner) {
    setNotice(""); setError("");
    try {
      const response = await request(`/practices/${practice.id}/declaration-subjects`, {
        method: "POST", body: JSON.stringify({ data: owner }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Salvataggio in anagrafica non riuscito");
      setNotice(data.created ? `${owner.name} aggiunto all'anagrafica clienti` : `${owner.name} è già presente in anagrafica`);
      if (data.created) setDirectory(list => [...list, data.client]);
    } catch (err) { setError(err.message); }
  }

  async function generate(fmt = "pdf") {
    setBusy(true); setError(""); setNotice("");
    try {
      const payload = { ...form, co_owners: form.co_owners.map(({ uid, ...rest }) => rest) };
      if (form.kind === "affittuario" && !form.owner_is_company) {
        Object.assign(payload, { company_name: "", company_vat: "", company_seat: "", company_cciaa: "", company_pec: "" });
      }
      const response = await fetch(`${API}/practices/${practice.id}/property-declaration/pdf?fmt=${fmt}`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: payload }),
      });
      if (!response.ok) throw new Error("Generazione del documento non riuscita");
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = url;
      if (fmt === "pdf") { link.target = "_blank"; link.rel = "noopener noreferrer"; }
      else link.download = "Autocertificazione.docx";
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      const cadastral = { cadastral_sheet: form.cadastral_sheet, cadastral_parcel: form.cadastral_parcel,
                          cadastral_sub: form.cadastral_sub, property_locality: form.property_locality };
      if (Object.values(cadastral).some(Boolean)) await onSaveCadastral(cadastral).catch(() => {});
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }

  return <section className="practice-section" data-testid="practice-property-declaration">
    <div className="practice-section-heading">
      <h2><FileBadge size={16} />Autocertificazioni</h2>
      <span>Dichiarazione sostitutiva art. 47 D.P.R. 445/2000 · i dati anagrafici arrivano dalle anagrafiche INGEGO</span>
    </div>
    {error && <div className="error" role="alert" data-testid="declaration-error">{error}</div>}
    {notice && <div className="practice-hint" data-testid="declaration-notice">{notice}</div>}

    <div className="declaration-modes">
      {KINDS.map(([value, label]) =>
        <button key={value} className={form.kind === value ? "active" : ""} data-testid={`declaration-kind-${value}`}
          onClick={() => set("kind", value)}>{label}</button>)}
      <label className="declaration-gender">Genere del dichiarante
        <select data-testid="declaration-gender" value={form.gender} onChange={e => set("gender", e.target.value)}>
          <option value="M">Maschile</option><option value="F">Femminile</option>
        </select>
      </label>
    </div>

    {rentOut && <>
      <div className="practice-hint" data-testid="declaration-rentout-hint">In questo modello dichiara <b>la proprietà</b>: attesta il titolo di proprietà e autorizza l'affittuario alla presentazione del progetto.</div>
      <label className="quote-switch">La proprietà è una società (dichiara l'amministratore)
        <input type="checkbox" data-testid="declaration-owner-is-company" checked={!!form.owner_is_company} onChange={e => set("owner_is_company", e.target.checked)} />
      </label>
    </>}

    {company && <div className="declaration-modes" data-testid="declaration-company-title">
      {[["proprietaria", "Società proprietaria"], ["affittuaria", "Società affittuaria"]].map(([value, label]) =>
        <button key={value} className={form.company_title === value ? "active" : ""} data-testid={`declaration-title-${value}`}
          onClick={() => set("company_title", value)}>{label}</button>)}
    </div>}

    <div className="practice-form-grid">
      {PERSON_FIELDS.map(([field, label]) => <label key={field}>{label}
        <input data-testid={`declaration-${field}`} value={form[field]} onChange={e => set(field, e.target.value)} />
      </label>)}
      {(company || ownerCompany) && COMPANY_FIELDS.map(([field, label]) => <label key={field}>{label}
        <input data-testid={`declaration-${field}`} value={form[field]} onChange={e => set(field, e.target.value)} />
      </label>)}
      {ASSET_FIELDS.map(([field, label]) => <label key={field}>{label}
        <input data-testid={`declaration-${field}`} value={form[field]} onChange={e => set(field, e.target.value)} />
      </label>)}
      {tenant && LEASE_FIELDS.map(([field, label]) => <label key={field}>{label}
        <input data-testid={`declaration-${field}`} value={form[field]} onChange={e => set(field, e.target.value)} />
      </label>)}
      {rentOut && [...TENANT_FIELDS, ...RENT_LEASE_FIELDS].map(([field, label]) => <label key={field}>{label}
        <input data-testid={`declaration-${field}`} value={form[field] || ""} onChange={e => set(field, e.target.value)} />
      </label>)}
      <label className="field-wide">Ulteriore dichiarazione (facoltativa)
        <textarea rows={2} data-testid="declaration-extra" value={form.extra_statement} onChange={e => set("extra_statement", e.target.value)} />
      </label>
    </div>

    {multi && <div className="declaration-owners" data-testid="declaration-owners">
      <div className="declaration-owners-bar">
        <strong>Altri comproprietari</strong>
        <select data-testid="declaration-owner-import" value={importId} onChange={e => setImportId(e.target.value)}>
          <option value="">Importa da anagrafica clienti…</option>
          {directory.map(item => <option key={item.client_id} value={item.client_id}>{item.name}</option>)}
        </select>
        <button type="button" data-testid="declaration-owner-import-confirm" disabled={!importId} onClick={importOwner}>
          <BookUser size={14} />Importa
        </button>
        <button type="button" data-testid="declaration-owner-add" onClick={() => setForm(f => ({ ...f, co_owners: [...f.co_owners, emptyOwner()] }))}>
          <Plus size={14} />Aggiungi soggetto
        </button>
      </div>
      {form.co_owners.length === 0 && <p className="practice-hint">Il primo dichiarante è quello indicato sopra. Aggiungi gli altri comproprietari che sottoscriveranno il documento.</p>}
      {form.co_owners.map((owner, index) => <div key={owner.uid} className="declaration-owner-card" data-testid={`declaration-owner-${index}`}>
        <div className="declaration-owner-head">
          <span>Comproprietario {index + 2}</span>
          <select data-testid={`declaration-owner-${index}-gender`} value={owner.gender} onChange={e => setOwner(owner.uid, "gender", e.target.value)}>
            <option value="M">Maschile</option><option value="F">Femminile</option>
          </select>
          <button type="button" data-testid={`declaration-owner-${index}-save`} disabled={!owner.name} onClick={() => saveOwnerToDirectory({ ...owner })}>
            <UserPlus size={14} />Salva in anagrafica
          </button>
          <button type="button" className="danger" data-testid={`declaration-owner-${index}-remove`} onClick={() => removeOwner(owner.uid)}>
            <Trash2 size={14} />Rimuovi
          </button>
        </div>
        <div className="practice-form-grid">
          {CO_OWNER_FIELDS.map(([field, label]) => <label key={field}>{label}
            <input data-testid={`declaration-owner-${index}-${field}`} value={owner[field]} onChange={e => setOwner(owner.uid, field, e.target.value)} />
          </label>)}
        </div>
      </div>)}
    </div>}

    {allowThirdParty && <>
      <label className="quote-switch">Autorizza un soggetto terzo alla presentazione del progetto
        <input type="checkbox" data-testid="declaration-authorize" checked={form.authorize_third_party} onChange={e => set("authorize_third_party", e.target.checked)} />
      </label>
      {form.authorize_third_party && <div className="practice-form-grid">
        <label>Soggetto autorizzato<input data-testid="declaration-third_party_name" value={form.third_party_name} onChange={e => set("third_party_name", e.target.value)} /></label>
        <label className="field-wide">Qualifica e dati del soggetto<input data-testid="declaration-third_party_detail" value={form.third_party_detail} onChange={e => set("third_party_detail", e.target.value)} /></label>
      </div>}
    </>}

    <div className="practice-inline-actions">
      <DocumentButton label="Genera autocertificazione" testId="declaration-generate" disabled={busy || !form.declarant_name}
        icon={<Printer size={15} />} onGenerate={generate} />
    </div>
  </section>;
};
